import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveMoney } from "@/utils/formatPrice";
import { useAuth } from "./useAuth";
import { useOrganization } from "./useOrganization";

// Sprint D Plan A — dual-state types: legacy kronor + _minor öre.
interface PlayerCreditsRow {
  balance: number;                     // legacy kronor
  balance_minor: number | null;        // öre (preferred post-cutover)
  currency: string;
  user_id: string;
  organization_id: string;
}

interface CreditTransactionRow {
  id: string;
  user_id: string;
  organization_id: string;
  amount: number;                      // legacy kronor
  amount_minor: number | null;         // öre
  currency: string;
  type: string;
  reference_id: string | null;
  description: string | null;
  created_at: string;
  expires_at: string | null;
}

export function usePlayerCredits() {
  const { user } = useAuth();
  const { organization } = useOrganization();

  const { data: credits, ...rest } = useQuery({
    queryKey: ["player-credits", user?.id, organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("player_credits" as any)
        .select("balance, balance_minor, currency, user_id, organization_id")
        .eq("user_id", user!.id)
        .eq("organization_id", organization!.id)
        .maybeSingle();
      if (error) throw error;
      return data as PlayerCreditsRow | null;
    },
    enabled: !!user?.id && !!organization?.id,
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ["credit-transactions", user?.id, organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("credit_transactions" as any)
        .select("id, user_id, organization_id, amount, amount_minor, currency, type, reference_id, description, created_at, expires_at")
        .eq("user_id", user!.id)
        .eq("organization_id", organization!.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data as CreditTransactionRow[]) ?? [];
    },
    enabled: !!user?.id && !!organization?.id,
  });

  // Calculate expired credits that should be subtracted from balance.
  // Sprint D dual-state: prefer _minor, fall back to legacy kronor.
  const now = new Date().toISOString();
  const txMoney = (tx: CreditTransactionRow) =>
    resolveMoney(tx.amount_minor, tx.amount, tx.currency)?.valueMinor ?? 0;
  const expiredMinor = transactions
    .filter((tx) => tx.expires_at && tx.expires_at < now && txMoney(tx) > 0)
    .reduce((sum, tx) => sum + txMoney(tx), 0);
  const deductedMinor = transactions
    .filter((tx) => tx.type === "expiry_deduction")
    .reduce((sum, tx) => sum + Math.abs(txMoney(tx)), 0);
  const undeductedExpiredMinor = Math.max(0, expiredMinor - deductedMinor);

  const storedMoney = resolveMoney(credits?.balance_minor, credits?.balance, credits?.currency);
  const storedBalanceMinor = storedMoney?.valueMinor ?? 0;
  const effectiveBalanceMinor = Math.max(0, storedBalanceMinor - undeductedExpiredMinor);

  // Backwards-compat: existing callers expect kronor numbers (since UI passes
  // them to formatPrice(number) — legacy form). Sprint E switches to Money.
  const storedBalance = Math.round(storedBalanceMinor / 100);
  const effectiveBalance = Math.round(effectiveBalanceMinor / 100);

  return {
    balance: effectiveBalance,
    storedBalance,
    transactions,
    ...rest,
  };
}
